/**
 * Flash trace — the record of every seam that can repaint the whole viewport.
 *
 * The founder sees "a flash that flashes the entire screen sometimes", on
 * every page, and nothing in the logs can see it: the seams that paint over
 * the whole viewport (the gate curtain, a .gate-takeover verdict, a shell
 * remount, a theme flip, an error boundary swapping its children) all do it
 * without a line anywhere. This leaf gives each of them one, so the NEXT
 * flash is readable instead of re-argued.
 *
 * Three cheap records per call, in the order that survives a failure best:
 *   - the ring buffer at `window.__vestraFlashTrace`, the last 50, read-only
 *     and pasteable straight out of the console;
 *   - a `[flash] …` console line, for a session with the console already open;
 *   - one `performance.mark('flash:<kind>')`, so the paint lands on the same
 *     timeline as the browser's own frames in a DevTools recording.
 *
 * Kinds, timestamps and the pathname only: no query string, no network, no
 * storage, nothing a user typed. A trace must never be the thing that breaks
 * the page, so the whole body is one try/catch — a browser without
 * `performance` records the first two and moves on.
 *
 * The buffer never leaves the browser by itself. `onFlashTrace` is the one
 * seam a reader subscribes through: the flash recorder (flash-report.ts)
 * listens here and ships the run-up to a disruption to the server.
 */

/** One traced seam: what painted, which one, when, and where. */
export interface FlashTraceEntry {
  kind: string;
  detail?: string;
  /**
   * Structured facts a detector attaches (counts, element summaries of tag,
   * test id and class). Never text content, never a URL.
   */
  data?: Record<string, string | number>;
  /** Wall clock, so a pasted buffer lines up with a server log. */
  at: number;
  /** Pathname alone — a query string can carry a token or an email. */
  path: string;
}

/** Long enough to hold the run-up to a flash, short enough to paste. */
const RING_SIZE = 50;

const ring: FlashTraceEntry[] = [];

const listeners = new Set<(entry: FlashTraceEntry) => void>();

/** Hear every entry from now on. Returns the unsubscribe. */
export function onFlashTrace(
  listener: (entry: FlashTraceEntry) => void
): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** A copy of the buffer, oldest first. */
export function readFlashTrace(): FlashTraceEntry[] {
  return ring.slice();
}

/** Publish the buffer as a getter: a reader gets a copy, never the record. */
function expose(): void {
  if (typeof window === "undefined") return;
  if (Object.prototype.hasOwnProperty.call(window, "__vestraFlashTrace"))
    return;
  Object.defineProperty(window, "__vestraFlashTrace", {
    get: () => ring.slice(),
    configurable: true,
  });
}

/** Record one full-viewport paint. Never throws; changes nothing else. */
export function traceFlash(
  kind: string,
  detail?: string,
  data?: Record<string, string | number>
): void {
  try {
    const entry: FlashTraceEntry = {
      kind,
      ...(detail === undefined ? {} : { detail }),
      ...(data === undefined ? {} : { data }),
      at: Date.now(),
      path: typeof location === "undefined" ? "" : location.pathname,
    };
    ring.push(entry);
    if (ring.length > RING_SIZE) ring.splice(0, ring.length - RING_SIZE);
    expose();
    for (const listener of listeners) {
      try {
        listener({ ...entry });
      } catch {
        // One reader's bug never costs the record or the next reader.
      }
    }
    console.debug(`[flash] ${kind}${detail === undefined ? "" : ` ${detail}`}`);
    performance.mark(`flash:${kind}`);
  } catch {
    // Diagnostics never break the page.
  }
}
